import './styles/CreateProductPage.scss';

import { Add, Edit, HelpOutline, InfoOutlined } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  ImageList,
  ImageListItem,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import { RelationType } from '@thro-types/access-handler';
import { CreateProduct, Product, Unit, ProductType, AvailabilityType } from '@thro-types/leihbar';

import { Field } from 'formik';
import omitDeep from 'omit-deep-lodash';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useEntitlementGroupsQuery } from 'api/hooks/accessHandler';
import {
  useLeihbarCategoriesQuery,
  useLocationsQuery,
  useProductMutation,
  useProductQuery
} from 'api/hooks/leihbar';
import { useSelfEntitlementGroupsQuery } from 'api/hooks/users/self';
import { Modal } from 'app/components';
import CreatePage from 'app/components/CreatePage';
import StepDescription from 'app/components/CreatePage/components/StepDescription';
import { Description } from 'app/components/Description';
import StyledDropzone from 'app/components/Dropzone';
import { RecurringIntervalList } from 'app/components/RecurringIntervalList';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import usePageHeading from 'utils/hooks/usePageHeading';

import { getMyLeihbarEntitlements, isLeihBarAdmin, uploadAttachmentToS3 } from '../../helper';
import { AvailabeUnitsReactionTime } from '../../leihbar.types';
import { BookingSlotsPicker } from './components/AvailabilitySelection';
import { BundleModal } from './components/BundleModal';
import { CategorySelect } from './components/CategorySelect';
import { ContactEmailForm } from './components/ContactEmailForm';
import { EditorAutocomplete } from './components/EditorAutocomplete';
import { TargetUserAutocomplete } from './components/TargetUserAutocomplete';
import { initialProductValues, productValidationSchema } from './validation';

enum AvailableUnits {
  HOUR = Unit.HOUR,
  DAY = Unit.DAY
}

export const CreateProductPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const { user: currentUser } = useAuthContext();

  const { data: entitlementGroups } = useEntitlementGroupsQuery();
  const { data: myEntitlementGroups, isSuccess } = useSelfEntitlementGroupsQuery();
  const { create, update } = useProductMutation();
  const { data: categories } = useLeihbarCategoriesQuery();
  const { data: locations } = useLocationsQuery();

  const myLeihbarEntitlements = getMyLeihbarEntitlements(myEntitlementGroups);

  const { data: product, isLoading, isError } = useProductQuery(parseInt(id || '0'));

  const { permissions } = useAuthContext();

  usePageHeading(
    {
      heading: product?.id
        ? t(translations.pages.leihbar.product.create.editHeading())
        : t(translations.pages.leihbar.product.create.createHeading()),
      headingProps: {
        back: location.state?.back ?? (product?.id ? `/leihbar/product/${product.id}` : '/leihbar')
      }
    },
    [product]
  );

  const handleBundleEdit = () => {
    setBundleModalOpen(true);
  };

  useEffect(() => {
    if (isError) {
      dispatch(
        AlertActions.error(
          `${t(translations.pages.leihbar.product.alerts.error.openCreateFailed())}`
        )
      );
      navigate('/leihbar', { replace: true });
    }
  }, [isError, dispatch, t, navigate]);

  if (isLoading) {
    return <CircularProgress />;
  }

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (setDisabledCancel) {
      setDisabledCancel(true);
    }

    if (typeof values.image !== 'string') {
      try {
        let uploadResult = await uploadAttachmentToS3({ file: values.image, type: 'ProductImage' });
        values.image = uploadResult[0];
      } catch (error) {
        dispatch(
          AlertActions.error(t(translations.pages.leihbar.product.alerts.failedImageUpload()))
        );
        setSubmitting(false);
        if (setDisabledCancel) {
          setDisabledCancel(false);
        }
        return;
      }
    }
    // Regular expression to match a leading zero
    const leadingZeroRegex = /^0/;

    // Replace the leading zero with '+49'
    values.lender.phone = values.lender.phone?.replace(leadingZeroRegex, '+49');

    values.locationId = values.location?.id;

    if (!!values.products?.length) {
      values.productIds = values.products.map(product => product.id);
    } else {
      values.productIds = [];
    }
    delete values.products;
    delete values.targetUsers;
    if (!values.reactionTime?.value) {
      delete values.reactionTime;
    }

    if (values.availabilityType !== AvailabilityType.BOOKINGSLOT) {
      values.possibleBookingSlots = [];
    } else if (values.availabilityType === AvailabilityType.BOOKINGSLOT) {
      values.recurringIntervals = [];
    }

    if (typeof values.maxPreBooking === 'string') {
      values.maxPreBooking = undefined;
    }

    if (values.id) {
      try {
        const omitFields = [
          'id',
          '__typename',
          'location',
          'createdAt',
          'updatedAt',
          'recurringIntervalProductId',
          'productId'
        ];
        await update.mutateAsync({
          id: values.id,
          data: { ...omitDeep(values, omitFields) }
        });
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.product.alerts.successfulUpdate()))
        );
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.product.alerts.failedUpdate())));
      }
    } else {
      try {
        const omitFields = ['id', '__typename', 'location'];
        await create.mutateAsync({
          ...omitDeep(values, omitFields)
        });
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.product.alerts.successfulCreate()))
        );
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.product.alerts.failedCreate())));
      }
    }
    setSubmitting(false);
    navigate(values.id ? `/leihbar/product/${values.id}` : '/leihbar');
  };

  const width = 10;
  return (
    <>
      <Helmet>
        <title>{t(translations.pages.leihbar.product.name())}</title>
      </Helmet>
      <CreatePage<CreateProduct & { products: Product[]; id?: number }>
        initialValues={initialProductValues(product, currentUser)}
        validationSchema={productValidationSchema}
        disabledCancel={disabledCancel}
        handleSaveClick={handleSaveClick}
        submittingState={[submitting, setSubmitting]}
        xs={width}
      >
        {({ values, setFieldValue, errors, touched }) => {
          const isLenderGroupIdDisabled =
            !location.state?.clone && !!values.lenderGroupId && !!values.id;
          if (
            isSuccess &&
            myLeihbarEntitlements.length === 1 &&
            !values.id &&
            values.lenderGroupId === null
          ) {
            setFieldValue('lenderGroupId', myLeihbarEntitlements[0].id);
          }

          return (
            <Grid container className="product-create-grid">
              <Modal
                width="full"
                open={bundleModalOpen}
                headline={t(translations.pages.leihbar.product.create.bundleHeading())}
                onCancel={() => setBundleModalOpen(false)}
                onClose={() => setBundleModalOpen(false)}
              >
                <BundleModal productId={product?.id} />
              </Modal>
              <Grid container>
                <StepDescription
                  title={t(
                    translations.pages.leihbar.product.create.steps.generalInformation.title()
                  )}
                  text={t(
                    translations.pages.leihbar.product.create.steps.generalInformation.text()
                  )}
                />
                <Grid container item component={Paper} xs={12} xl={6}>
                  <Grid item xs={12} xl={6}>
                    <FormControl required style={{ paddingBottom: '1em' }}>
                      <FormLabel>
                        {t(translations.pages.leihbar.product.create.productname())}
                      </FormLabel>
                      <Field
                        as={TextField}
                        id="name"
                        name="name"
                        size="small"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} xl={6}>
                    <FormControl required sx={{ maxWidth: '100%' }}>
                      <FormLabel>{t(translations.pages.leihbar.product.create.image())}</FormLabel>
                      <StyledDropzone
                        name="image"
                        value={values.image}
                        setFieldValue={setFieldValue}
                        errors={errors}
                        placeholder={t(
                          translations.pages.leihbar.product.create.placeholder.uploadImage()
                        )}
                        className="product-create__dropzone"
                        showPreview
                        accept={{
                          'image/jpeg': ['.jpeg', '.jpg'],
                          'image/png': ['.png'],
                          'image/svg+xml': ['.svg']
                        }}
                        single
                        size="small"
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} xl={width}>
                <Divider />
              </Grid>
              <Grid container>
                <StepDescription
                  title={t(translations.pages.leihbar.product.create.steps.description.title())}
                  text={t(translations.pages.leihbar.product.create.steps.description.text())}
                />
                <Grid container item component={Paper} xs={12} xl={6}>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <FormLabel>
                        {t(translations.pages.leihbar.product.create.description())}
                      </FormLabel>
                      <Field name="description" id="description">
                        {({ field }) => <Description field={field} />}
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} xl={width}>
                <Divider />
              </Grid>
              <Grid container>
                <StepDescription
                  title={t(translations.pages.leihbar.product.create.steps.amount.title())}
                  text={t(translations.pages.leihbar.product.create.steps.amount.text())}
                />
                <Grid item container component={Paper} xs={12} xl={6} gap={2} alignItems={'center'}>
                  <FormControl required>
                    <FormControlLabel
                      id="isAmountPossible"
                      name={t(translations.pages.leihbar.product.create.amountPossible())}
                      control={
                        <Switch
                          checked={values.isAmountPossible}
                          onChange={_e => {
                            setFieldValue('isAmountPossible', !values.isAmountPossible);
                          }}
                        />
                      }
                      label={t(translations.pages.leihbar.product.create.amountPossible())}
                    />
                  </FormControl>
                  {values.isAmountPossible && (
                    <FormControl
                      disabled={!values.isAmountPossible}
                      className="product-create__appearAnimation"
                    >
                      <FormLabel style={{ display: 'flex', gap: 4 }}>
                        {t(translations.pages.leihbar.product.create.maxArticleAmount())}
                        <Tooltip
                          title={t(
                            translations.pages.leihbar.product.create.steps.amount.maxAmountInfo()
                          )}
                        >
                          <HelpOutline style={{ opacity: 0.8 }} />
                        </Tooltip>
                      </FormLabel>
                      <Grid container alignItems={'center'} gap={3}>
                        <Grid item>
                          <Field
                            disabled={!values.isAmountPossible}
                            as={TextField}
                            type="number"
                            id="maxArticleAmount"
                            name="maxArticleAmount"
                            InputProps={{
                              inputProps: { min: 1 }
                            }}
                            size="small"
                            error={touched.maxArticleAmount && Boolean(errors.maxArticleAmount)}
                            helperText={touched.maxArticleAmount && errors.maxArticleAmount}
                          />
                        </Grid>
                      </Grid>
                    </FormControl>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} xl={width}>
                <Divider />
              </Grid>
              <Grid container>
                <StepDescription
                  title={t(translations.pages.leihbar.product.create.steps.type.title())}
                  text={t(translations.pages.leihbar.product.create.steps.type.text())}
                />
                <Grid
                  item
                  container
                  component={Paper}
                  xs={12}
                  xl={6}
                  direction={'column'}
                  minHeight={'5em'}
                >
                  <FormControl style={{ paddingBottom: '1em' }} required>
                    <FormLabel>{t(translations.pages.leihbar.product.create.type())}</FormLabel>
                    <Field as={Select} id="productType" name="productType" fullWidth size="small">
                      {Object.values(ProductType).map(type => (
                        <MenuItem key={type} value={type}>
                          {t(translations.pages.leihbar.product.create.types[type]())}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item xs={12} xl={width}>
                <Divider />
              </Grid>
              {values.productType === ProductType.BUNDLE && (
                <>
                  <Grid container className="product-create__appearAnimation">
                    <StepDescription
                      title={t(
                        translations.pages.leihbar.product.create.steps.bundleProducts.title()
                      )}
                      text={t(
                        translations.pages.leihbar.product.create.steps.bundleProducts.text()
                      )}
                    />
                    <Grid
                      item
                      container
                      component={Paper}
                      xs={12}
                      xl={6}
                      direction={'column'}
                      minHeight={'9em'}
                    >
                      <FormControl style={{ paddingBottom: '1em' }} required>
                        <FormLabel>
                          {t(translations.pages.leihbar.product.create.bundleProducts())}
                        </FormLabel>
                        <Grid container justifyContent={'space-between'} alignItems={'center'}>
                          <Grid item xl={3}>
                            <Button
                              variant="contained"
                              style={{ color: 'white' }}
                              startIcon={<Edit />}
                              onClick={handleBundleEdit}
                            >
                              {t(translations.common.edit())}
                            </Button>
                          </Grid>
                          <Grid item xs={12} xl={9}>
                            <ImageList cols={3}>
                              {!!values.products?.length ? (
                                values.products?.map(product => {
                                  const imgSrc = `${import.meta.env.VITE_S3_IMAGE_PROXY}/${
                                    product.image
                                  }`;
                                  return (
                                    <ImageListItem key={product.image}>
                                      <img
                                        src={imgSrc}
                                        srcSet={imgSrc}
                                        alt={product.name}
                                        loading="lazy"
                                      />
                                    </ImageListItem>
                                  );
                                })
                              ) : (
                                <>
                                  {t(
                                    translations.pages.leihbar.product.bundle.noProductsSelected()
                                  )}
                                </>
                              )}
                            </ImageList>
                          </Grid>
                        </Grid>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} xl={width} className="product-create__appearAnimation">
                    <Divider />
                  </Grid>
                </>
              )}
              <Grid container>
                <StepDescription
                  title={t(translations.pages.leihbar.product.create.steps.category.title())}
                  text={t(translations.pages.leihbar.product.create.steps.category.text())}
                />
                <Grid item component={Paper} xs={12} xl={6}>
                  <FormControl required fullWidth>
                    <FormLabel>{t(translations.pages.leihbar.product.create.category())}</FormLabel>
                    <Grid container item xs={12}>
                      {categories?.map((cat, index) => {
                        return <CategorySelect category={cat} key={index} />;
                      })}
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item xs={12} xl={width}>
                <Divider />
              </Grid>
              <Grid container>
                <StepDescription
                  title={t(
                    translations.pages.leihbar.product.create.steps.domainAndLocation.title()
                  )}
                  text={t(translations.pages.leihbar.product.create.steps.domainAndLocation.text())}
                />
                <Grid
                  item
                  container
                  component={Paper}
                  xs={12}
                  xl={6}
                  direction={'column'}
                  minHeight={'9em'}
                >
                  <FormControl style={{ paddingBottom: '1em' }} required>
                    <FormLabel>{t(translations.pages.leihbar.product.create.domain())}</FormLabel>
                    <Tooltip
                      title={
                        isLenderGroupIdDisabled
                          ? t(translations.pages.leihbar.product.tooltips.lenderGroupIdDisabled())
                          : ''
                      }
                    >
                      <Grid item xs={6}>
                        <Autocomplete
                          id="lenderGroupId"
                          fullWidth
                          size="small"
                          options={
                            isLeihBarAdmin(permissions) && entitlementGroups
                              ? entitlementGroups?.data.filter(
                                  ent => ent.relationType === RelationType.LEIHBAR
                                )
                              : myEntitlementGroups
                          }
                          value={
                            entitlementGroups?.data.find(ent => ent.id === values.lenderGroupId) ||
                            null
                          }
                          getOptionLabel={option => option.name}
                          renderInput={params => <TextField {...params} />}
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                          onChange={(_, value) => {
                            setFieldValue('lenderGroupId', value?.id);
                          }}
                          disabled={isLenderGroupIdDisabled}
                        />
                      </Grid>
                    </Tooltip>
                  </FormControl>
                  <FormControl required>
                    <FormLabel>{t(translations.pages.leihbar.product.create.location())}</FormLabel>
                    <Grid item xs={6}>
                      <Autocomplete
                        id="location"
                        fullWidth
                        size="small"
                        value={values.location}
                        getOptionLabel={location => `${location.name}`}
                        options={locations || []}
                        renderInput={params => <TextField {...params} />}
                        onChange={(_, value) => {
                          if (value && typeof value !== 'string') {
                            setFieldValue('locationId', value.id);
                            setFieldValue('location', value);
                          } else {
                            setFieldValue('locationId', null);
                            setFieldValue('location', null);
                          }
                        }}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      />
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item xs={12} xl={width}>
                <Divider />
              </Grid>
              <Grid container>
                <StepDescription
                  title={t(translations.pages.leihbar.product.create.steps.bookingCriteria.title())}
                  text={t(translations.pages.leihbar.product.create.steps.bookingCriteria.text())}
                />
                <Grid
                  item
                  container
                  component={Paper}
                  xs={12}
                  xl={6}
                  direction={'column'}
                  minHeight={'5em'}
                >
                  <FormControl
                    style={{ paddingBottom: '1em' }}
                    required={!values.targetUserIds.length}
                  >
                    <FormLabel>
                      {t(translations.pages.leihbar.product.create.bookingCriteria())}
                    </FormLabel>
                    <Autocomplete
                      id="visibilityGroupIds"
                      fullWidth
                      size="small"
                      multiple
                      options={entitlementGroups?.data || []}
                      value={values.visibilityGroupIds?.map(value => {
                        return { id: value } as any;
                      })}
                      getOptionLabel={option =>
                        option.name
                          ? `${option.name}`
                          : `${entitlementGroups?.data.find(eg => eg.id === option.id)?.name}`
                      }
                      renderInput={params => <TextField {...params} />}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(_, value) => {
                        const idArray = value.map(item => item.id);
                        setFieldValue('visibilityGroupIds', idArray);
                      }}
                    />
                  </FormControl>
                  <FormControl
                    style={{ paddingBottom: '1em' }}
                    required={!values.visibilityGroupIds.length}
                  >
                    <FormLabel>
                      {t(translations.pages.leihbar.product.create.targetUsers())}
                    </FormLabel>
                    <TargetUserAutocomplete />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item xs={10}>
                <Divider />
              </Grid>
              <Grid container>
                <StepDescription
                  title={t(translations.pages.leihbar.product.create.steps.bookingSlots.title())}
                  text={t(translations.pages.leihbar.product.create.steps.bookingSlots.text())}
                />
                <Grid
                  container
                  item
                  component={Paper}
                  xs={12}
                  xl={6}
                  justifyContent={'space-between'}
                >
                  <FormControl fullWidth>
                    <FormLabel>
                      {t(translations.pages.leihbar.product.create.availability())}
                    </FormLabel>
                    <Grid item xs={12}>
                      <FormControlLabel
                        id="isIndependentOfLenderTimes"
                        name="isIndependentOfLenderTimes"
                        control={
                          <Switch
                            checked={values.isIndependentOfLenderTimes}
                            onChange={(_e, checked) => {
                              setFieldValue(
                                'isIndependentOfLenderTimes',
                                !values.isIndependentOfLenderTimes
                              );
                            }}
                          />
                        }
                        label={t(
                          translations.pages.leihbar.product.create.independentOfLenderTimes()
                        )}
                      />
                      <Tooltip
                        title={t(
                          translations.pages.leihbar.product.create.steps.intervals.isIndependentOfLenderTimes()
                        )}
                      >
                        <HelpOutline style={{ opacity: 0.5 }} />
                      </Tooltip>
                    </Grid>
                  </FormControl>
                  <FormControl fullWidth disabled={!values.isIndependentOfLenderTimes}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        id="alwaysAvailable"
                        name="alwaysAvailable"
                        control={
                          <Switch
                            disabled={!values.isIndependentOfLenderTimes}
                            checked={values.availabilityType === AvailabilityType.ALWAYS}
                            onChange={(_e, checked) => {
                              setFieldValue(
                                'availabilityType',
                                checked ? AvailabilityType.ALWAYS : AvailabilityType.BOOKINGSLOT
                              );
                              if (!values.possibleBookingSlots.length) {
                                values.possibleBookingSlots = [
                                  { start: new Date(), end: new Date() }
                                ];
                              }
                            }}
                          />
                        }
                        label={t(translations.pages.leihbar.product.create.alwaysAvailable())}
                      />
                    </Grid>
                  </FormControl>
                  {values.availabilityType !== AvailabilityType.ALWAYS &&
                    values.isIndependentOfLenderTimes && (
                      <>
                        <Grid item xs={3} />
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={values.availabilityType}
                          onChange={(e, newType) =>
                            newType !== null && setFieldValue('availabilityType', newType)
                          }
                        >
                          <ToggleButton value={AvailabilityType.BOOKINGSLOT}>
                            {t(translations.pages.leihbar.product.create.pickers.startend())}
                          </ToggleButton>
                          <ToggleButton value={AvailabilityType.RECURRINGINTERVAL}>
                            {t(
                              translations.pages.leihbar.product.create.pickers.recurringInterval()
                            )}
                          </ToggleButton>
                        </ToggleButtonGroup>
                        <Grid item xs={1} />
                        <Grid container item xs={1} justifyContent="end">
                          <Button
                            variant="outlined"
                            onClick={() => {
                              if (values.availabilityType === AvailabilityType.BOOKINGSLOT) {
                                let updatedBookingSlots = [...values.possibleBookingSlots];
                                updatedBookingSlots.push({
                                  start: new Date(),
                                  end: new Date()
                                });
                                setFieldValue('possibleBookingSlots', updatedBookingSlots);
                              } else if (
                                values.availabilityType === AvailabilityType.RECURRINGINTERVAL
                              ) {
                                const newIntervals = [...values.recurringIntervals];
                                newIntervals.push({ weekdays: [], start: 480, end: 960 });
                                setFieldValue('recurringIntervals', newIntervals);
                              }
                            }}
                          >
                            <Add />
                          </Button>
                        </Grid>
                        {values.availabilityType === AvailabilityType.RECURRINGINTERVAL && (
                          <FormControl fullWidth>
                            <FormLabel>
                              {t(translations.pages.leihbar.product.create.availability())}
                            </FormLabel>
                            <RecurringIntervalList
                              fieldName="recurringIntervals"
                              values={values.recurringIntervals}
                              setFieldValue={setFieldValue}
                            />
                          </FormControl>
                        )}
                        {values.availabilityType === AvailabilityType.BOOKINGSLOT && (
                          <FormControl required fullWidth>
                            <FormLabel>
                              {t(translations.pages.leihbar.product.create.bookingSlot.name())}
                            </FormLabel>
                            <BookingSlotsPicker />
                          </FormControl>
                        )}
                      </>
                    )}
                </Grid>
              </Grid>
              <Grid item xs={12} xl={width}>
                <Divider />
              </Grid>
              <Grid container>
                <StepDescription
                  title={t(translations.pages.leihbar.product.create.steps.intervals.title())}
                  text={t(translations.pages.leihbar.product.create.steps.intervals.text())}
                />
                <Grid
                  item
                  container
                  component={Paper}
                  xs={12}
                  xl={6}
                  direction={'column'}
                  minHeight={'9em'}
                  gap={2}
                >
                  <FormControl required>
                    <FormLabel>
                      {t(translations.pages.leihbar.product.create.maxBooking())}
                    </FormLabel>
                    <Grid container>
                      <Grid item xs={2}>
                        <Field
                          as={TextField}
                          size="small"
                          type="number"
                          id="maxBooking.value"
                          name="maxBooking.value"
                          InputProps={{
                            inputProps: { min: 1 }
                          }}
                          error={touched.maxBooking && Boolean(errors.maxBooking)}
                          helperText={touched.maxBooking && errors.maxBooking?.value}
                        />
                      </Grid>
                      <Grid item xs={4} marginLeft={'1em'}>
                        <Field
                          as={Select}
                          id="maxBooking.unit"
                          name="maxBooking.unit"
                          fullWidth
                          size="small"
                        >
                          {Object.values(AvailableUnits).map(unit => (
                            <MenuItem key={unit} value={unit}>
                              {t(translations.pages.leihbar.product.create.units[unit]())}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                    </Grid>
                  </FormControl>
                  <FormControl required>
                    <Grid container gap={1}>
                      <FormLabel style={{ display: 'flex', gap: 4 }}>
                        {t(translations.pages.leihbar.product.create.preparationTime())}
                      </FormLabel>
                      <Tooltip
                        title={t(
                          translations.pages.leihbar.product.create.steps.intervals.preparationTimeInfo()
                        )}
                      >
                        <HelpOutline style={{ opacity: 0.5 }} />
                      </Tooltip>
                    </Grid>
                    <Grid container>
                      <Grid item xs={2}>
                        <Field
                          as={TextField}
                          type="number"
                          id="preparationTime.value"
                          name="preparationTime.value"
                          InputProps={{
                            inputProps: { min: 0 }
                          }}
                          size="small"
                          error={touched.preparationTime && Boolean(errors.preparationTime)}
                          helperText={touched.preparationTime && errors.preparationTime?.value}
                        />
                      </Grid>
                      <Grid item xs={4} marginLeft={'1em'}>
                        <Field
                          as={Select}
                          id="preparationTime.unit"
                          name="preparationTime.unit"
                          fullWidth
                          size="small"
                        >
                          {Object.values(AvailableUnits).map(unit => (
                            <MenuItem key={unit} value={unit}>
                              {t(translations.pages.leihbar.product.create.units[unit]())}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                      {values.id && (
                        <Grid container item xs={4} flex={1} alignItems={'center'} marginLeft={3}>
                          <Tooltip
                            title={t(
                              translations.pages.leihbar.product.create.steps.intervals.editInfo()
                            )}
                          >
                            <InfoOutlined fontSize="large" style={{ opacity: 0.8 }} />
                          </Tooltip>
                        </Grid>
                      )}
                    </Grid>
                  </FormControl>
                  <FormControl>
                    <Grid container gap={1}>
                      <FormLabel style={{ display: 'flex', gap: 4 }}>
                        {t(translations.pages.leihbar.product.create.reactionTime())}
                      </FormLabel>
                      <Tooltip
                        title={t(
                          translations.pages.leihbar.product.create.steps.intervals.reactionTimeInfo()
                        )}
                      >
                        <HelpOutline style={{ opacity: 0.5 }} />
                      </Tooltip>
                    </Grid>
                    <Grid container>
                      <Grid item xs={2}>
                        <Field
                          as={TextField}
                          size="small"
                          type="number"
                          id="reactionTime.value"
                          name="reactionTime.value"
                          InputProps={{
                            inputProps: { min: 0 }
                          }}
                          error={touched.reactionTime && Boolean(errors.reactionTime)}
                          helperText={touched.reactionTime && errors.reactionTime}
                        />
                      </Grid>
                      <Grid item xs={4} marginLeft={'1em'}>
                        <Field
                          as={Select}
                          id="reactionTime.unit"
                          name="reactionTime.unit"
                          fullWidth
                          size="small"
                        >
                          {Object.values(AvailabeUnitsReactionTime).map(unit => (
                            <MenuItem key={unit} value={unit}>
                              {t(translations.pages.leihbar.product.create.units[unit]())}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                    </Grid>
                  </FormControl>
                  <FormControl>
                    <FormLabel style={{ display: 'flex', gap: 4 }}>
                      {t(translations.pages.leihbar.product.create.maxPreBooking())}
                      <Tooltip
                        title={t(
                          translations.pages.leihbar.product.create.steps.intervals.maxPrebookingInfo()
                        )}
                      >
                        <HelpOutline style={{ opacity: 0.8 }} />
                      </Tooltip>
                    </FormLabel>
                    <Grid container alignItems={'center'} gap={3}>
                      <Grid item xs={2}>
                        <Field
                          as={TextField}
                          type="number"
                          id="maxPreBooking"
                          name="maxPreBooking"
                          InputProps={{
                            inputProps: { min: 0 }
                          }}
                          size="small"
                          error={touched.maxPreBooking && Boolean(errors.maxPreBooking)}
                          helperText={touched.maxPreBooking && errors.maxPreBooking}
                        />
                      </Grid>
                      {t(translations.common.days())}
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item xs={12} xl={width}>
                <Divider />
              </Grid>
              <Grid container>
                <StepDescription
                  title={t(translations.pages.leihbar.product.create.steps.lender.title())}
                  text={t(translations.pages.leihbar.product.create.steps.lender.text())}
                />
                <Grid
                  item
                  container
                  component={Paper}
                  xs={12}
                  xl={6}
                  direction={'column'}
                  minHeight={'9em'}
                >
                  <FormControl style={{ paddingBottom: '1em' }} required>
                    <FormLabel>
                      {t(translations.pages.leihbar.product.create.lender.email())}
                    </FormLabel>
                    <ContactEmailForm />
                  </FormControl>
                  <FormControl style={{ paddingBottom: '1em' }}>
                    <FormLabel>
                      {t(translations.pages.leihbar.product.create.lender.number())}
                    </FormLabel>
                    <Grid container>
                      <Grid item xs={12} md={6}>
                        <Field
                          as={TextField}
                          id="lender.phone"
                          name="lender.phone"
                          placeholder={t(
                            translations.pages.leihbar.product.create.placeholder.enterNumber()
                          )}
                          fullWidth
                          size="small"
                          value={values.lender?.phone || ''}
                          onChange={e => setFieldValue('lender.phone', `${e.target.value}`)}
                          error={touched.lender?.phone && Boolean(errors.lender?.phone)}
                          helperText={touched.lender?.phone && errors.lender?.phone}
                        />
                      </Grid>
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item xs={12} xl={width}>
                <Divider />
              </Grid>
              <Grid container>
                <StepDescription
                  title={t(translations.pages.leihbar.product.create.steps.editorGroup.title())}
                  text={t(translations.pages.leihbar.product.create.steps.editorGroup.text())}
                />
                <Grid
                  item
                  container
                  component={Paper}
                  xs={12}
                  xl={6}
                  direction={'column'}
                  minHeight={'5em'}
                >
                  <FormControl style={{ paddingBottom: '1em' }}>
                    <FormLabel>
                      {t(translations.pages.leihbar.product.create.editorGroupId())}
                    </FormLabel>
                    <EditorAutocomplete />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item xs={12} xl={width}>
                <Divider />
              </Grid>
              <Grid container>
                <StepDescription
                  title={t(translations.pages.leihbar.product.create.steps.more.title())}
                  text={t(translations.pages.leihbar.product.create.steps.more.text())}
                />
                <Grid item container component={Paper} xs={12} xl={6}>
                  {/* <Grid item xs={12}>
                        <FormControlLabel
                          id="lender.isBookingMailActive"
                          name="lender.isBookingMailActive"
                          control={
                            <Switch
                              checked={values.lender?.isBookingMailActive || false}
                              onChange={(_e, checked) => {
                                setFieldValue('lender.isBookingMailActive', checked);
                              }}
                            />
                          }
                          label={t(translations.pages.leihbar.product.create.bookingMailActive())}
                        />
                      </Grid> */}
                  <Grid item xs={12}>
                    <FormControl style={{ paddingBottom: '1em' }} fullWidth>
                      <FormLabel>{t(translations.pages.leihbar.product.create.hint())}</FormLabel>
                      <Field
                        as={TextField}
                        id="hint"
                        name="hint"
                        fullWidth
                        size="small"
                        placeholder={t(
                          translations.pages.leihbar.product.create.placeholder.hint()
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel style={{ display: 'flex', gap: 4 }}>
                        {t(translations.pages.leihbar.product.create.steps.internalNote.title())}
                        <Tooltip
                          title={t(
                            translations.pages.leihbar.product.create.steps.internalNote.text()
                          )}
                        >
                          <InfoOutlined style={{ opacity: 0.8 }} />
                        </Tooltip>
                      </FormLabel>
                      <Field
                        as={TextField}
                        id="internalNote"
                        name="internalNote"
                        fullWidth
                        size="small"
                        placeholder={t(
                          translations.pages.leihbar.product.create.placeholder.internalNote()
                        )}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          );
        }}
      </CreatePage>
    </>
  );
};
